import { provide, inject, ref } from "vue";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation";
import axios from "axios";

const symbol = Symbol("mobileApp");

const defaultNotifyOptions = {
    visible: false,
    message: "",
    type: "success",
    location: "bottom center",
    timeout: 3000,
};

function useMobileAppState() {
    const { isLoading, manageAsyncOperation } = useAsyncOperation();

    const liveCountriesMap = ref(new Map());
    const isInitializing = ref(false);
    const isInitialized = ref(false);

    const notify = ref({ ...defaultNotifyOptions });

    function fetchLiveCountries() {
        return manageAsyncOperation(async () => {
            const { data } = await axios.get("/countries");
            const countries = data?.data?.lists ?? [];
            liveCountriesMap.value = new Map(
                countries.filter((c) => c.is_live).map((c) => [c.id, c]),
            );
        });
    }

    async function initializeApp(reinitialize = false) {
        if (!reinitialize && isInitialized.value) return;
        return manageAsyncOperation(
            async () => {
                await Promise.all([fetchLiveCountries()]);
                isInitialized.value = true;
            },
            null,
            (loading) => {
                isInitializing.value = loading;
            },
            true,
        );
    }

    function showNotify(message, type = "success") {
        notify.value.visible = true;
        notify.value.message = message;
        notify.value.type = type;
    }

    function hideNotify() {
        notify.value = { ...defaultNotifyOptions };
    }

    async function fetchTimezonesBasedOnCountry(countryId = null) {
        return manageAsyncOperation(async () => {
            const { data } = await axios.get("/timezones", {
                params: {
                    country_id: countryId,
                },
            });
            return new Map(data?.data?.map((t) => [t.timezone, t]));
        });
    }

    return {
        liveCountriesMap,
        isLoading,
        isInitializing,
        isInitialized,
        initializeApp,
        notify,
        showNotify,
        hideNotify,
        fetchTimezonesBasedOnCountry,
    };
}

export function provideMobileApp() {
    const state = useMobileAppState();
    provide(symbol, state);
    return state;
}

export function useMobileApp() {
    return inject(symbol);
}
